// BM-H595 (23 Sep 2026): the automatic AI check of a new draft against the
// FORF runs on the server after the first build. This hook follows it: while
// the status is "running" it asks the server every 3 seconds, and hands the
// finished result (changes made, suggestions, findings, the swept letter) to
// the editor. The status lives on the submission, so reloading the page
// mid-check simply picks it up again.
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API } from "../config/api";

export const PRESWEEP_POLL_MS = 3000;

// BM-H3747 (5 Oct 2026, BL.VEC.006.1555): every first-draft suggestion used
// to reach the rail as "advice only", which is what Vanessa saw as "make any
// change by hand". Where the AI wrote replacement wording for a known place
// and only held back from applying it itself, the card now offers it as a
// one-click replace (refused if that paragraph has changed since). Loose
// comments with no wording are dropped when the full once-over has run as
// part of the first go, because it covers the same ground with placed fixes;
// they stay when it did not run.
export function presweepCards(data, ts = Date.now()) {
  const reviewRan = data?.review_status === "done";
  const out = [];
  (data?.suggestions || []).forEach((sug, i) => {
    const placeable = Boolean(sug.proposed_text && sug.target_text && sug.block_id);
    if (!placeable && reviewRan) return;
    out.push({
      id: `presweep-${i}`,
      anchorId: null,
      kind: "warning",
      title: sug.label || "Check against the FORF",
      body: sug.reason || "",
      quote: sug.target_text || null,
      op: placeable ? "replace" : "advisory",
      blockId: placeable ? sug.block_id : null,
      blockText: placeable ? sug.block_text || null : null,
      targetText: placeable ? sug.target_text : null,
      suggestedText: placeable ? sug.proposed_text : null,
      drafting: false,
      severity: "medium",
      gapLabel: placeable ? sug.label || "Suggested wording" : "",
      gapReason: placeable ? sug.reason || "" : "",
      timestamp: ts,
      status: "open",
      advisory: !placeable,
    });
  });
  return out;
}

export function useCclPresweep({ submissionId, initial, onFinished }) {
  const [state, setState] = useState(() => ({
    status: initial?.status || "",
    reason: initial?.reason || "",
    changes: initial?.changes || [],
    suggestions: initial?.suggestions || [],
    findings: initial?.findings || [],
  }));
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  // Seed from the ccl-content response once it arrives.
  const seed = useCallback((presweep) => {
    if (!presweep) return;
    setState((prev) => ({
      ...prev,
      status: presweep.status || "",
      reason: presweep.reason || "",
      changes: presweep.changes || prev.changes,
      suggestions: presweep.suggestions || prev.suggestions,
      findings: presweep.findings || prev.findings,
    }));
  }, []);

  useEffect(() => {
    if (state.status !== "running" || !submissionId) return undefined;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/submissions/${submissionId}/ccl-presweep`);
        if (cancelled) return;
        const data = res.data || {};
        if (data.status && data.status !== "running") {
          setState({
            status: data.status,
            reason: data.reason || "",
            changes: data.changes || [],
            suggestions: data.suggestions || [],
            findings: data.findings || [],
          });
          finishedRef.current?.(data);
        }
      } catch {
        // A failed poll is retried on the next tick; the server marks the
        // check failed itself after 120 seconds.
      }
    }, PRESWEEP_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [state.status, submissionId]);

  const clearChanges = useCallback(() => {
    setState((prev) => ({ ...prev, changes: [] }));
  }, []);

  const removeChange = useCallback((id) => {
    setState((prev) => ({ ...prev, changes: prev.changes.filter((c) => c.id !== id) }));
  }, []);

  return { ...state, running: state.status === "running", seed, clearChanges, removeChange };
}
